/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * <scrollable-table>
 * ------------------------------------------------------------------------------------------------------------
 *
 * Wraps a horizontally scrolling table and reports, as attributes, whether there is anything to scroll to. The
 * CSS that shows a "scroll for more" hint keys off those attributes, so the hint appears only when it is true
 * and gets out of the way once the reader has reached the end.
 *
 *   <scrollable-table>
 *     <div data-scrollable-table-scroller>...</div>
 *     <p class="...">Scroll for more</p>
 *   </scrollable-table>
 *
 * Attributes set on the element:
 *   data-ready       - the element measured itself, so CSS can stop guessing from viewport width alone
 *   data-overflowing - the content is wider than the box
 *   data-at-end      - scrolled as far as it goes
 */
class ScrollableTable extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-scrollable-table-scroller]');

    if (!this.scroller) {
      return;
    }

    this._update = this._update.bind(this);
    this.scroller.addEventListener('scroll', this._update, { passive: true });

    if ('ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(this._update);
      this._resizeObserver.observe(this.scroller);
    } else {
      window.addEventListener('resize', this._update);
    }

    this.setAttribute('data-ready', '');
    this._update();
  }

  disconnectedCallback() {
    if (!this.scroller) {
      return;
    }

    this.scroller.removeEventListener('scroll', this._update);
    window.removeEventListener('resize', this._update);
    this._resizeObserver && this._resizeObserver.disconnect();
  }

  _update() {
    const scrollable = this.scroller.scrollWidth - this.scroller.clientWidth;

    // Sub-pixel layout rounding leaves a stray fraction on tables that actually fit.
    const overflowing = scrollable > 1;

    // scrollLeft runs negative in right-to-left locales, so compare the distance.
    const atEnd = overflowing && Math.abs(this.scroller.scrollLeft) >= scrollable - 1;

    this.toggleAttribute('data-overflowing', overflowing);
    this.toggleAttribute('data-at-end', atEnd);
  }
}

if (!customElements.get('scrollable-table')) {
  customElements.define('scrollable-table', ScrollableTable);
}

/**
 * ------------------------------------------------------------------------------------------------------------
 * <wood-species-explorer>
 * ------------------------------------------------------------------------------------------------------------
 *
 * Drives sections/wood-species.liquid: a swatch grid with "used for" filter chips, where each swatch opens a
 * native <dialog> with that wood's details. The section renders everything server-side; this only adds:
 *
 *   - filtering: chips carry data-filter (a handleized tag, "" for all); grid items carry data-uses (space-
 *     separated handleized tags) and are hidden when they don't match
 *   - deep links: opening a wood sets #<handle> in the URL, and a page loaded with that hash opens it
 *   - previous / next inside the dialog, stepping through the woods the current filter shows
 */
class WoodSpeciesExplorer extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('li[data-wood]'));
    this.chips = Array.from(this.querySelectorAll('[data-filter]'));
    this.count = this.querySelector('[data-count]');
    this.empty = this.querySelector('[data-empty]');

    this.addEventListener('click', this._onClick.bind(this));

    this.querySelectorAll('dialog').forEach((dialog) => {
      // A click that lands on the dialog element itself is a click on the backdrop.
      dialog.addEventListener('click', (event) => event.target === dialog && dialog.close());
      dialog.addEventListener('close', () => this._clearHash(dialog));
    });

    this._onHashChange = this._openFromHash.bind(this);
    window.addEventListener('hashchange', this._onHashChange);
    this._openFromHash();
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this._onHashChange);
  }

  _onClick(event) {
    const chip = event.target.closest('[data-filter]');
    const card = event.target.closest('[aria-controls]');
    const step = event.target.closest('[data-step]');

    if (chip) {
      this._filter(chip);
    } else if (card) {
      this._open(card.getAttribute('aria-controls').replace(/^wood-/, ''));
    } else if (step) {
      this._step(step.closest('dialog'), Number(step.dataset.step));
    } else if (event.target.closest('[data-close]')) {
      event.target.closest('dialog').close();
    }
  }

  _filter(chip) {
    const tag = chip.dataset.filter;
    let shown = 0;

    this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
    this.items.forEach((item) => {
      const match = !tag || item.dataset.uses.split(' ').includes(tag);
      item.hidden = !match;
      shown += match ? 1 : 0;
    });

    if (this.count) {
      this.count.textContent = tag ? `${shown} of ${this.items.length} woods` : `${this.items.length} woods`;
    }
    if (this.empty) {
      this.empty.hidden = shown > 0;
    }
  }

  _visibleHandles() {
    return this.items.filter((item) => !item.hidden).map((item) => item.dataset.wood);
  }

  _open(handle) {
    const dialog = this.querySelector(`dialog[data-wood="${CSS.escape(handle)}"]`);

    if (!dialog) {
      return;
    }

    this.querySelectorAll('dialog[open]').forEach((open) => open !== dialog && open.close());
    this._updateStepLabels(dialog);

    if (!dialog.open) {
      dialog.showModal();
    }

    dialog.scrollTop = 0;

    if (location.hash !== `#${handle}`) {
      history.replaceState(null, '', `#${handle}`);
    }
  }

  _step(dialog, direction) {
    const handles = this._visibleHandles();
    const next = handles[handles.indexOf(dialog.dataset.wood) + direction];

    if (next) {
      this._open(next);
    }
  }

  _updateStepLabels(dialog) {
    const handles = this._visibleHandles();
    const index = handles.indexOf(dialog.dataset.wood);

    dialog.querySelectorAll('[data-step]').forEach((button) => {
      const target = handles[index + Number(button.dataset.step)];
      const name = target && this.querySelector(`li[data-wood="${CSS.escape(target)}"] .wood-species__name`);

      button.hidden = !name;
      button.querySelector('[data-step-label]').textContent = name ? name.textContent : '';
    });
  }

  _openFromHash() {
    const handle = decodeURIComponent(location.hash.slice(1));

    if (handle && this.querySelector(`dialog[data-wood="${CSS.escape(handle)}"]`)) {
      this._open(handle);
    }
  }

  _clearHash(dialog) {
    if (location.hash === `#${dialog.dataset.wood}`) {
      history.replaceState(null, '', location.pathname + location.search);
    }
  }
}

if (!customElements.get('wood-species-explorer')) {
  customElements.define('wood-species-explorer', WoodSpeciesExplorer);
}
